import React from 'react';
import LegalLayout from './LegalLayout';

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="FABTEXS AI Privacy Policy"
      description="How FABTEXS AI — operated by FABTEXS – India’s Wholesale Fabric Hub — collects, uses, stores, and protects information about textile wholesalers and their customers on the FABTEXS WhatsApp commerce platform."
      lastUpdated="18 February 2026"
    >
      <p>
        This Privacy Policy explains how <strong>FABTEXS – India’s Wholesale Fabric Hub</strong>{' '}
        (&quot;<strong>FABTEXS</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;, or &quot;<strong>our</strong>&quot;) collects,
        uses, stores, discloses, and protects information when you (&quot;<strong>seller</strong>&quot;,
        &quot;<strong>vendor</strong>&quot;, or &quot;<strong>you</strong>&quot;) sign up for or use <strong>FABTEXS AI</strong>, our
        WhatsApp commerce automation platform for Indian textile wholesalers, available at{' '}
        <a href="https://app.fabtexs.com">app.fabtexs.com</a> and{' '}
        <a href="https://www.fabtexs.com">www.fabtexs.com</a> (collectively, the &quot;<strong>Platform</strong>&quot;).
      </p>

      <p>
        FABTEXS AI is designed for B2B textile sellers who use WhatsApp Business to message
        their buyers. We act as a <strong>data processor</strong> for the customer data sellers
        bring into the Platform (buyer phone numbers, names, conversation history) and as a{' '}
        <strong>data fiduciary / controller</strong> for the account data sellers provide about
        themselves (email, business name, payment information).
      </p>

      <h2>1. Information we collect</h2>

      <h3>1.1 Information you provide as a seller</h3>
      <ul>
        <li><strong>Account data</strong>: name, business name, email address, mobile number, password (stored as a one-way bcrypt hash, never in plaintext), business address, GSTIN (optional), and plan selection.</li>
        <li><strong>Catalog data</strong>: product images, product codes, descriptions, prices, sizes, categories, stock quantities, and other inventory you upload to FABTEXS.</li>
        <li><strong>Customer data you import</strong>: phone numbers, contact names, optional city / tags / category interests of the buyers you choose to manage through FABTEXS — supplied by you via CSV, Excel, paste-text, or screenshot OCR.</li>
        <li><strong>Order &amp; payment data</strong>: order details, invoice amounts, payment confirmations, and shipping addresses generated by you or your buyers through the Platform.</li>
        <li><strong>Support correspondence</strong>: messages you send us via in-product support or email.</li>
      </ul>

      <h3>1.2 Information we receive from Meta (WhatsApp Business)</h3>
      <p>
        When you connect a WhatsApp Business number — either by pasting Meta Cloud API
        credentials or via Facebook Login for Business (Embedded Signup) — we receive from
        Meta on your behalf:
      </p>
      <ul>
        <li>Your WhatsApp Business Account (WABA) ID, phone number ID, verified business name, display phone number, and quality rating.</li>
        <li>An access token that is encrypted at rest using AES-256 and used only to send messages on your behalf and to receive inbound webhooks from Meta.</li>
        <li>Incoming WhatsApp messages, message status events (sent / delivered / read), and media you or your buyers exchange through the connected number.</li>
      </ul>
      <p>
        We do <strong>not</strong> receive your Facebook personal profile, friend lists, ads data, or any
        information about Facebook pages you don&apos;t connect to FABTEXS.
      </p>

      <h3>1.3 Information we collect automatically</h3>
      <ul>
        <li><strong>Technical &amp; usage data</strong>: IP address, browser type, device type, OS, pages visited, session duration, and feature usage, gathered through standard server logs and first-party cookies.</li>
        <li><strong>Diagnostic data</strong>: error stack traces, latency metrics, and broadcast / webhook delivery statistics needed to keep the Platform reliable.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <p>We use the information described above to:</p>
      <ul>
        <li>Provide, operate, and improve the Platform — including AI-assisted reply drafting (Claude Sonnet 4.5 / Haiku 4.5), broadcast scheduling, screenshot OCR (Claude Vision), and analytics.</li>
        <li>Send and receive WhatsApp messages between you and your buyers strictly through your connected WhatsApp Business number.</li>
        <li>Authenticate your account, prevent fraud, enforce rate limits, and protect the Platform from abuse.</li>
        <li>Process subscription payments through PCI-DSS compliant payment processors (Razorpay).</li>
        <li>Send service emails (e.g. activation confirmation, billing receipts, security alerts). We do <strong>not</strong> use your data for unrelated marketing without your separate, explicit opt-in.</li>
        <li>Comply with applicable laws and lawful requests from regulators or courts.</li>
      </ul>

      <h2>3. Legal basis (under India&apos;s DPDP Act 2023 and similar laws)</h2>
      <p>We process personal data on the following legal bases:</p>
      <ul>
        <li><strong>Performance of a contract</strong>: to deliver the Platform features you signed up for.</li>
        <li><strong>Legitimate interest</strong>: to keep the Platform secure, reliable, and free of abuse.</li>
        <li><strong>Consent</strong>: where required (e.g., optional marketing communications). You can withdraw consent at any time via your account settings or by emailing us.</li>
        <li><strong>Legal obligation</strong>: tax, accounting, and lawful regulatory requirements.</li>
      </ul>

      <h2>4. Sharing of information</h2>
      <p>We share data only when necessary, and never sell personal data. Specifically:</p>
      <ul>
        <li><strong>Meta Platforms Inc. / WhatsApp LLC</strong> — to deliver messages your business sends through your own WhatsApp Business number, per Meta&apos;s WhatsApp Business Solution Terms.</li>
        <li><strong>Anthropic, PBC</strong> — content you submit to the AI assistant (product descriptions, conversation snippets, screenshots) is sent to Claude models for processing under Anthropic&apos;s data protection terms. Anthropic does not train its models on your data.</li>
        <li><strong>Razorpay Software Pvt Ltd</strong> — to process subscription payments. Card details are handled directly by Razorpay; we never store full card numbers, CVVs, or PINs.</li>
        <li><strong>Cloud infrastructure</strong> — MongoDB Atlas (encrypted at rest), AWS / Emergent-managed servers in Mumbai or Singapore regions for hosting.</li>
        <li><strong>Professional advisors</strong> — accountants, auditors, and legal counsel under strict confidentiality.</li>
        <li><strong>Government and law enforcement</strong> — only where compelled by valid Indian legal process.</li>
        <li><strong>Successors</strong> — in a merger, acquisition, or asset sale, your information may be transferred to the successor entity under the same protections set out here.</li>
      </ul>

      <h2>5. Your customers&apos; data — your responsibility</h2>
      <p>
        When you import customer phone numbers and broadcast to them through FABTEXS,
        <strong> you are the data fiduciary / controller</strong> for those buyers. You confirm and warrant that:
      </p>
      <ul>
        <li>You have lawful basis (typically consent) to message every buyer you import.</li>
        <li>You comply with WhatsApp&apos;s Business Messaging Policy, including its prohibition on unsolicited messages.</li>
        <li>You will honor opt-out requests within a reasonable timeframe.</li>
        <li>You will not import phone lists scraped from third parties or otherwise obtained without consent.</li>
      </ul>
      <p>
        FABTEXS will, at your direction, delete customer records and stop messaging on request.
        Buyers who wish to exercise rights over their data should first contact <strong>you</strong> as the seller;
        we will support reasonable buyer requests forwarded by you.
      </p>

      <h2>6. Data retention</h2>
      <ul>
        <li><strong>Account &amp; catalog data</strong>: retained for as long as your account is active, and for up to 18 months after closure for tax / audit purposes, then deleted.</li>
        <li><strong>WhatsApp messages &amp; media</strong>: retained for 90 days by default to power the inbox and AI history features. You can shorten this in Settings.</li>
        <li><strong>Encrypted access tokens</strong>: deleted within 7 days of you disconnecting a number.</li>
        <li><strong>Backups</strong>: rolling 30-day encrypted backups, then permanent deletion.</li>
      </ul>

      <h2>7. Security</h2>
      <ul>
        <li>TLS 1.2+ for all data in transit.</li>
        <li>AES-256 encryption at rest for sensitive fields including WhatsApp access tokens.</li>
        <li>Bcrypt password hashing with per-user salts.</li>
        <li>Role-based access controls inside FABTEXS — production database access is restricted to a small set of authorized engineers under written confidentiality agreements.</li>
        <li>Automated webhook signature verification (Meta X-Hub-Signature-256) and rate limiting on all sensitive endpoints.</li>
        <li>Quarterly security review of dependencies and configuration.</li>
      </ul>
      <p>
        No system is 100% secure. If we become aware of a data breach affecting your personal
        data, we will notify you and the Indian Data Protection Board within the timeframe
        required by law.
      </p>

      <h2>8. Your rights</h2>
      <p>Subject to applicable law (India DPDP Act 2023, GDPR where applicable), you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate personal data.</li>
        <li>Erase personal data we are not legally required to retain.</li>
        <li>Withdraw consent for any processing based on consent.</li>
        <li>Receive a copy of your data in a portable format (CSV / JSON).</li>
        <li>Lodge a complaint with the Indian Data Protection Board.</li>
      </ul>
      <p>
        To exercise these rights, email us at{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
        with the subject &quot;Data Rights Request&quot;. We will respond within 30 days.
      </p>

      <h2>9. Cookies</h2>
      <p>
        FABTEXS uses first-party cookies only — strictly for authentication (storing your
        signed JWT session token) and for remembering UI preferences. We do <strong>not</strong> set
        third-party advertising cookies on the SaaS application. The public marketing website
        may use privacy-preserving analytics (e.g. server-side page-view counts).
      </p>

      <h2>10. Children</h2>
      <p>
        FABTEXS is a B2B platform intended for textile businesses run by adults. We do not
        knowingly collect data from anyone under 18. If you believe a minor has signed up,
        email us and we will delete the account.
      </p>

      <h2>11. International transfers</h2>
      <p>
        Your data is stored primarily on servers located in India and Singapore. Where data
        is processed by our sub-processors (Meta, Anthropic, Razorpay) it may transit through
        servers located outside India. We rely on Standard Contractual Clauses and the
        sub-processor&apos;s own privacy commitments to safeguard such transfers.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be
        notified by email and a banner inside the Platform at least 7 days before the new
        version takes effect. The &quot;Last updated&quot; date at the top of this page reflects the
        current version.
      </p>

      <h2>13. Contact &amp; Grievance Officer</h2>
      <p>
        For any questions about this Privacy Policy, to exercise your data rights, or to
        raise a grievance under the DPDP Act, please contact:
      </p>
      <p>
        <strong>Grievance Officer — FABTEXS AI</strong><br />
        FABTEXS – India’s Wholesale Fabric Hub<br />
        Email: <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a><br />
        Subject line: &quot;Grievance — FABTEXS AI&quot;
      </p>
      <p>
        We are committed to resolving any grievance within 30 days of receipt.
      </p>
    </LegalLayout>
  );
}
